import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './FirmAuth.css';
import { apiCall } from '../utils/api';
import trackEvent from '../utils/analytics';

const EXPERTISE_OPTIONS = [
    'Audit', 'Tax', 'Advisory', 'Risk & Forensics',
    'Corporate Finance', 'Transfer Pricing', 'Tax Litigation', 'Compliance'
];

const FirmAuth = ({ onLoginSuccess, onBack }) => {
    const [authMode, setAuthMode] = useState('login'); // 'login', 'register'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Login form state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Registration form state
    const [regPhone, setRegPhone] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regFirmName, setRegFirmName] = useState('');
    const [regAddress, setRegAddress] = useState('');
    const [regPartnerName, setRegPartnerName] = useState('');
    const [regFirmId, setRegFirmId] = useState('');
    const [isCreatingFirm, setIsCreatingFirm] = useState(false);
    const [newFirmCity, setNewFirmCity] = useState('');
    const [newFirmPartners, setNewFirmPartners] = useState('');
    
    // Firm suggestions
    const [firmMasters, setFirmMasters] = useState([]);
    const [firmSuggestions, setFirmSuggestions] = useState([]);
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [showFirmSuggestions, setShowFirmSuggestions] = useState(false);

    const API_BASE = process.env.REACT_APP_SERVER_URL || 'https://api-dev.chaimate.ai';

    useEffect(() => {
        fetchFirmMasters();
    }, []);

    const fetchFirmMasters = async () => {
        try {
            const response = await apiCall.get('/firm/masters');
            if (response.status && response.data) {
                setFirmMasters(response.data || []);
            }
        } catch (e) {
            console.error('Error fetching firm masters:', e);
        }
    };

    const fetchFirmSuggestions = async (query) => {
        if (!query || query.length < 2) {
            setFirmSuggestions([]);
            return;
        }
        
        // Filter from masters list first
        const mastersFiltered = firmMasters.filter(firm => 
            firm.firm_name?.toLowerCase().includes(query.toLowerCase())
        );
        
        if (mastersFiltered.length > 0) {
            setFirmSuggestions(mastersFiltered.slice(0, 10));
            setShowFirmSuggestions(true);
            return;
        }

        // Fallback to ICAI search if no matches in masters
        try {
            const url = `${API_BASE}/icai/search?field=firm_name&value=${encodeURIComponent(query)}&limit=10`;
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                setFirmSuggestions(Array.isArray(data) ? data : data?.data || []);
                setShowFirmSuggestions(true);
            }
        } catch (e) {
            console.error('Error fetching firm suggestions:', e);
        }
    };

    const selectFirm = (firm) => {
        setRegFirmName(firm.firm_name || '');
        setPartnerOptions(firm.partners || []);
        setRegAddress(firm.address || '');
        setShowFirmSuggestions(false);
        setFirmSuggestions([]);
        setIsCreatingFirm(false);
        setNewFirmCity(firm.city || '');
        setNewFirmPartners((firm.partners || []).join(', '));
    };

    const getFriendlyRegistrationError = (rawMessage) => {
        if (!rawMessage) return '';
        const normalized = rawMessage.toLowerCase();
        if (normalized.includes('auth token')) {
            return 'An account with this email or phone number already exists. Please log in instead.';
        }
        if (normalized.includes('already exists') || normalized.includes('exists')) {
            return 'An account with this email or phone number already exists. Please log in instead.';
        }
        return rawMessage;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginEmail || !loginPassword) {
            setFormError('Please fill in all fields');
            return;
        }

        setIsSubmitting(true);
        setFormError('');
        trackEvent('firm_login_attempted', {});

        try {
            const isEmail = loginEmail.includes('@');
            const payload = isEmail 
                ? { email: loginEmail, password: loginPassword }
                : { phone_number: loginEmail, password: loginPassword };
            
            const response = await apiCall.post('/firm/login', JSON.stringify(payload));

            console.log('Firm login response:', response);
            
            // Extract tokens - check multiple possible locations
            const tokenData = response.data || response;
            const accessToken = tokenData.access_token || tokenData.accessToken || response.access_token;
            const refreshToken = tokenData.refresh_token || tokenData.refreshToken || response.refresh_token;
            
            console.log('Token data:', tokenData);
            console.log('Access token:', accessToken);
            console.log('Refresh token:', refreshToken);
            
            if (accessToken) {
                const accessTokenExpires = new Date(Date.now() + 1 * 864e5).toUTCString();
                document.cookie = `access_token=${accessToken}; path=/; expires=${accessTokenExpires}`;
                console.log('Cookie set, current cookies:', document.cookie);
                
                if (refreshToken) {
                    const refreshTokenExpires = new Date(Date.now() + 7 * 864e5).toUTCString();
                    document.cookie = `refresh_token=${refreshToken}; path=/; expires=${refreshTokenExpires}`;
                }
                localStorage.setItem('user_type', 'firm');
                trackEvent('firm_login_success', {});
                onLoginSuccess && onLoginSuccess('firm');
            } else if (response.status === false || response.status === 'error') {
                setFormError(response.message || 'Login failed. Please try again.');
            } else {
                setFormError('Login failed. No token received.');
            }
        } catch (error) {
            console.error('Firm login error:', error);
            setFormError('Login failed. Please check your credentials.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPassword = () => {
        trackEvent('firm_forgot_password_clicked', {});
        window.location.href = 'https://www.chaimate.ai/contact-us';
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        if (!regPhone && !regEmail) {
            setFormError('Please provide phone number or email');
            return;
        }
        if (!regPassword) {
            setFormError('Please enter a password');
            return;
        }
        if (!regFirmName) {
            setFormError('Please select a firm');
            return;
        }
        if (!regAddress) {
            setFormError('Please enter the firm address');
            return;
        }
        if (isCreatingFirm && !newFirmCity.trim()) {
            setFormError('Please enter the city for the new firm');
            return;
        }

        setIsSubmitting(true);
        setFormError('');
        trackEvent('firm_register_attempted', {});

        let icaiRecord = null;
        if (isCreatingFirm) {
            const partnerList = newFirmPartners
                .split(',')
                .map(p => p.trim())
                .filter(Boolean);

            try {
                const response = await apiCall.post('/icai', JSON.stringify({
                    firm_name: regFirmName.trim(),
                    city: newFirmCity.trim(),
                    address: regAddress,
                    partners: partnerList
                }));
                icaiRecord = response.data || response;
                if (icaiRecord?._id) {
                    setRegFirmId(icaiRecord._id);
                }
            } catch (icaiErr) {
                console.error('ICAI record creation error:', icaiErr);
                setFormError('Could not create ICAI record for the firm. Please try again.');
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const payload = {
                phone_number: regPhone,
                email: regEmail,
                password: regPassword,
                firm_name: regFirmName,
                address: regAddress,
                partner: regPartnerName,
                firm_id: regFirmId
            };

            if (isCreatingFirm) {
                payload.city = newFirmCity.trim();
                const partnerList = newFirmPartners
                    .split(',')
                    .map(p => p.trim())
                    .filter(Boolean);
                if (partnerList.length) {
                    payload.partners = partnerList;
                }
            }

            const response = await apiCall.post('/firm/create', JSON.stringify(payload));

            console.log('Firm create response:', response);
            
            // Extract tokens - check multiple possible locations
            const tokenData = response.data || response;
            const accessToken = tokenData.access_token || tokenData.accessToken || response.access_token;
            const refreshToken = tokenData.refresh_token || tokenData.refreshToken || response.refresh_token;
            
            console.log('Token data:', tokenData);
            console.log('Access token:', accessToken);
            console.log('Refresh token:', refreshToken);
            
            if (accessToken) {
                const accessTokenExpires = new Date(Date.now() + 1 * 864e5).toUTCString();
                document.cookie = `access_token=${accessToken}; path=/; expires=${accessTokenExpires}`;
                console.log('Cookie set, current cookies:', document.cookie);
                
                if (refreshToken) {
                    const refreshTokenExpires = new Date(Date.now() + 7 * 864e5).toUTCString();
                    document.cookie = `refresh_token=${refreshToken}; path=/; expires=${refreshTokenExpires}`;
                }
                localStorage.setItem('user_type', 'firm');
                trackEvent('firm_register_success', {});
                onLoginSuccess && onLoginSuccess('firm');
            } else if (response.status === false || response.status === 'error') {
                const friendly = getFriendlyRegistrationError(response.message);
                setFormError(friendly || 'Registration failed. Please try again.');
            } else {
                setFormError('An account with this email or phone number already exists. Please log in instead.');
            }
        } catch (error) {
            console.error('Firm registration error:', error);
            // Most registration failures (duplicate account, etc.) end up here
            setFormError('An account with this email or phone number already exists. Please log in instead.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderLoginMode = () => (
        <form className="firm-auth-form" onSubmit={handleLogin}>
            <h2 className="firm-auth-title">Firm Login</h2>
            
            <div className="firm-auth-field">
                <label>Phone Number / Email</label>
                <input
                    type="text"
                    placeholder="Enter phone or email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    disabled={isSubmitting}
                />
            </div>

            <div className="firm-auth-field">
                <label>Password</label>
                <input
                    type="password"
                    placeholder="Enter password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    disabled={isSubmitting}
                />
            </div>

            {formError && authMode === 'login' && <div className="firm-auth-error">{formError}</div>}

            <button 
                type="submit" 
                className="firm-auth-btn firm-auth-btn-primary"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Logging in...' : 'Login'}
            </button>

            <button
                type="button"
                className="firm-auth-forgot-btn"
                onClick={handleForgotPassword}
            >
                Forgot password?
            </button>

            <button 
                type="button" 
                className="firm-auth-btn firm-auth-btn-secondary"
                onClick={() => { setAuthMode('register'); setFormError(''); }}
            >
                Create Account
            </button>

            {onBack && (
                <button 
                    type="button" 
                    className="firm-auth-back-link"
                    onClick={onBack}
                >
                    ← Back to User Login
                </button>
            )}
        </form>
    );

    const renderRegisterMode = () => (
        <form className="firm-auth-form" onSubmit={handleRegister}>
            <h2 className="firm-auth-title">Create Firm Account</h2>
            
            <div className="firm-auth-field">
                <label>Phone Number</label>
                <input
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={regPhone}
                    onChange={(e) => setRegPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    disabled={isSubmitting}
                />
            </div>

            <div className="firm-auth-field">
                <label>Email</label>
                <input
                    type="email"
                    placeholder="firm@example.com"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    disabled={isSubmitting}
                />
            </div>

            <div className="firm-auth-field">
                <label>Password</label>
                <input
                    type="password"
                    placeholder="Create a password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    disabled={isSubmitting}
                />
            </div>

            <div className="firm-auth-field">
                <label>Firm Name</label>
                <div className="firm-auth-autocomplete">
                    <input
                        type="text"
                        placeholder="Search for your firm..."
                        value={regFirmName}
                        onChange={(e) => {
                            setRegFirmName(e.target.value);
                            fetchFirmSuggestions(e.target.value);
                        }}
                        onFocus={() => firmSuggestions.length > 0 && setShowFirmSuggestions(true)}
                        disabled={isSubmitting}
                    />
                    {showFirmSuggestions && firmSuggestions.length > 0 && (
                        <div className="firm-auth-suggestions">
                            {firmSuggestions.map((firm, idx) => (
                                <div 
                                    key={idx} 
                                    className="firm-auth-suggestion"
                                    onClick={() => selectFirm(firm)}
                                >
                                    <strong>{firm.firm_name}</strong>
                                    <small>{firm.city} • {firm.address?.slice(0, 50)}...</small>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="firm-auth-field firm-auth-create-link">
                <button
                    type="button"
                    className="firm-auth-link-btn"
                    onClick={() => {
                        setIsCreatingFirm(prev => {
                            if (!prev) {
                                setNewFirmCity('');
                                setNewFirmPartners('');
                            }
                            return !prev;
                        });
                    }}
                    disabled={isSubmitting}
                >
                    {isCreatingFirm ? 'Cancel adding new firm' : "Can't find your firm? Create it"}
                </button>
            </div>

            <div className="firm-auth-field">
                <label>Address</label>
                <input
                    type="text"
                    placeholder="Firm address"
                    value={regAddress}
                    onChange={(e) => setRegAddress(e.target.value)}
                    disabled={isSubmitting}
                    readOnly={regFirmId ? true : false}
                    className={regFirmId ? 'firm-auth-readonly' : ''}
                />
            </div>

            {isCreatingFirm && (
                <>
                    <div className="firm-auth-field">
                        <label>City</label>
                        <input
                            type="text"
                            placeholder="City for the new firm"
                            value={newFirmCity}
                            onChange={(e) => setNewFirmCity(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="firm-auth-field">
                        <label>Partners (comma separated)</label>
                        <input
                            type="text"
                            placeholder="Partner names"
                            value={newFirmPartners}
                            onChange={(e) => setNewFirmPartners(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>
                </>
            )}

            {!isCreatingFirm && (
                <div className="firm-auth-field">
                    <label>Partner Name</label>
                    <select
                        value={regPartnerName}
                        onChange={(e) => setRegPartnerName(e.target.value)}
                        disabled={isSubmitting}
                    >
                        <option value="">Select partner</option>
                        <option value="all_partners">All Partner Names</option>
                        <option value="hr">HR</option>
                        {partnerOptions.map((partner) => (
                            <option key={partner} value={partner}>
                                {partner}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="firm-auth-field">
                <label>Firm ID (ICAI)</label>
                <input
                    type="text"
                    placeholder="ICAI Firm Registration Number"
                    value={regFirmId}
                    onChange={(e) => setRegFirmId(e.target.value)}
                    disabled={isSubmitting}
                />
            </div>

            {formError && <div className="firm-auth-error">{formError}</div>}

            <button 
                type="submit" 
                className="firm-auth-btn firm-auth-btn-primary"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </button>

            <button 
                type="button" 
                className="firm-auth-back-link"
                onClick={() => { setAuthMode('login'); setFormError(''); }}
            >
                ← Back to Login
            </button>
        </form>
    );

    const content = (
        <div className="firm-auth-card">
            {authMode === 'login' && renderLoginMode()}
            {authMode === 'register' && renderRegisterMode()}
        </div>
    );

    if (authMode === 'register') {
        return (
            <>
                <div className="firm-auth-container">
                    <div className="firm-auth-card">
                        {renderLoginMode()}
                    </div>
                </div>
                {ReactDOM.createPortal(
                    <div className="firm-auth-portal-overlay">
                        <div className="firm-auth-card">
                            {renderRegisterMode()}
                        </div>
                    </div>,
                    document.body
                )}
            </>
        );
    }

    return (
        <div className="firm-auth-container">
            {content}
        </div>
    );
};

export default FirmAuth;
